import React, { useState } from "react";
import { toast } from "sonner";
import api, { BACKEND_URL } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { X, Upload, Loader2 } from "lucide-react";

const BACKEND = BACKEND_URL;
const CANTONS = ["VD", "GE", "VS", "FR", "NE", "JU", "BE", "ZH", "LU", "SG", "TI", "BS", "BL", "AG", "GR", "AI", "AR", "GL", "NW", "OW", "SH", "SO", "SZ", "TG", "UR", "ZG"];
const OBJ_CATS = ["tents", "furniture", "costumes", "crafts", "instruments", "other"];

export default function CreateListingDialog({ open, onClose, onCreated }) {
  const { t } = useLang();
  const empty = { kind: "sell", title: "", description: "", price: "", condition: "used", object_category: "other", canton: "" };
  const [form, setForm] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  if (!open) return null;

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const room = 5 - photos.length;
    if (room <= 0) { toast.error(t("lst_max_photos")); return; }
    setUploading(true);
    const added = [];
    for (const f of files.slice(0, room)) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/community/listings/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(`${BACKEND}${data.url}`);
      } catch { toast.error(t("lst_form_error")); }
    }
    setPhotos((p) => [...p, ...added].slice(0, 5));
    setUploading(false);
    e.target.value = "";
  };

  const submit = async () => {
    if (form.title.trim().length < 3) { toast.error(t("lst_form_title_required")); return; }
    setBusy(true);
    try {
      const payload = {
        kind: form.kind, title: form.title.trim(), description: form.description.trim(),
        object_category: form.object_category, canton: form.canton || undefined, photos,
        price_cents: form.kind === "sell" && form.price !== "" ? Math.round(parseFloat(form.price) * 100) : null,
        condition: form.kind === "sell" ? form.condition : null,
      };
      const { data } = await api.post("/community/listings", payload);
      toast.success(t("lst_form_saved"));
      setForm(empty); setPhotos([]);
      onCreated?.(data);
    } catch (e) { toast.error(e?.response?.data?.detail || t("lst_form_error")); } finally { setBusy(false); }
  };

  const field = "w-full bg-[#0B0C0E] border border-[#272A34] rounded-md px-3 py-2.5 text-sm text-stone-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500";
  return (
    <div className="fixed inset-0 z-[1200] flex items-end sm:items-center justify-center bg-black/70 p-0 sm:p-4" onClick={onClose}>
      <div className="w-full sm:max-w-lg bg-[#121418] border border-[#272A34] rounded-t-2xl sm:rounded-xl p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="create-listing-dialog">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-xl text-amber-100">{t("lst_form_title")}</h3>
          <button onClick={onClose} data-testid="create-listing-close" className="text-stone-400 hover:text-stone-200"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div className="flex gap-2">
            {["sell", "search"].map((k) => (
              <button key={k} data-testid={`listing-form-kind-${k}`} onClick={() => setForm({ ...form, kind: k })} className={`flex-1 px-3 py-2 rounded-md text-sm ${form.kind === k ? "bg-amber-600 text-stone-950 font-semibold" : "bg-[#0B0C0E] text-stone-300 border border-[#272A34]"}`}>{t(`lst_kind_${k}`)}</button>
            ))}
          </div>
          <input data-testid="listing-form-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={t("lst_form_title_ph")} className={field} />
          <textarea data-testid="listing-form-desc" rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder={t("lst_form_desc_ph")} className={`${field} resize-none`} />
          {form.kind === "sell" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-stone-500 mb-1">{t("lst_form_price")}</label>
                <input data-testid="listing-form-price" type="number" min="0" step="1" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={field} />
              </div>
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-stone-500 mb-1">{t("lst_form_condition")}</label>
                <select data-testid="listing-form-condition" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className={field}>
                  <option value="new">{t("lst_cond_new")}</option><option value="used">{t("lst_cond_used")}</option>
                </select>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-stone-500 mb-1">{t("lst_form_category")}</label>
              <select data-testid="listing-form-category" value={form.object_category} onChange={(e) => setForm({ ...form, object_category: e.target.value })} className={field}>
                {OBJ_CATS.map((c) => <option key={c} value={c}>{t(`lst_cat_${c}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-stone-500 mb-1">{t("lst_form_canton")}</label>
              <select data-testid="listing-form-canton" value={form.canton} onChange={(e) => setForm({ ...form, canton: e.target.value })} className={field}>
                <option value="">—</option>
                {CANTONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-stone-500 mb-1">{t("lst_form_photos")}</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {photos.map((p, i) => (
                <div key={i} className="relative w-16 h-16 rounded-md overflow-hidden border border-[#272A34]">
                  <img src={p} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))} className="absolute top-0 right-0 bg-black/70 text-white p-0.5"><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
            {photos.length < 5 && (
              <label data-testid="listing-form-photos" className="inline-flex items-center gap-2 cursor-pointer bg-[#0B0C0E] border border-dashed border-[#272A34] hover:border-amber-500/40 rounded-md px-3 py-2 text-sm text-stone-300">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} {uploading ? t("lst_uploading") : t("lst_form_add_photos")}
                <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} disabled={uploading} />
              </label>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-stone-300 border border-[#272A34]">{t("com_cancel")}</button>
            <button data-testid="listing-form-submit" onClick={submit} disabled={busy || uploading || form.title.trim().length < 3} className="bg-amber-600 hover:bg-amber-500 text-stone-950 font-semibold px-5 py-2 rounded-md text-sm disabled:opacity-50">{t("lst_form_submit")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
